import {
	EquipmentSpec,
	GemColor,
	ItemSlot,
	ItemSpec,
	ItemSwap,
	PresetEncounter,
	PresetTarget,
	SimDatabase,
} from '../proto/common.js';
import {
	GlyphID,
	IconData,
	UIDatabase,
	UIEnchant as Enchant,
	UIGem as Gem,
	UIItem as Item,
	UINPC as Npc,
	UISet,
	UISetDatabase,
	UIZone as Zone,
} from '../proto/ui.js';

import {
	getEligibleEnchantSlots,
	getEligibleItemSlots,
} from './utils.js';
import { gemEligibleForSocket, gemMatchesSocket } from './gems.js';
import { EquippedItem } from './equipped_item.js';
import { Gear, ItemSwapGear } from './gear.js';
import { CHARACTER_LEVEL } from '../constants/mechanics.js';
import { distinct } from '../utils.js';
import { getWowheadLanguagePrefix, getLanguageCode } from '../constants/lang.js';

const dbUrlBase = '/wotlk/assets/database/';
const iconsBasePath = '/wotlk/assets/icons/';
// When changing this value, don't forget to change the html <link> for preloading!
const READ_JSON = true;
const EDITOR_API_URL = (typeof window !== 'undefined' && (window as any).EDITOR_API_URL) || `${window.location.protocol}//${window.location.hostname}:8090`;

export class Database {
	private static loadedDBs: Map<string, Promise<Database>> = new Map();
	private static loadingLeftovers: Map<string, Promise<Database>> = new Map();
	private static currentLanguage: string = 'en';

	static async get(lang?: string): Promise<Database> {
		const language = lang || getLanguageCode() || 'en';

		// If already loading/loaded, return the existing promise
		if (Database.loadedDBs.has(language)) {
			return Database.loadedDBs.get(language)!;
		}

		// Create the loading promise and cache it immediately
		const loadPromise = (async () => {
			const dbSuffix = language === 'en' ? '' : `_${language}`;

			// First: load base data (enchants, gems, zones) from local db.json
			// We still need these as API doesn't serve them yet
			const dbUrlJson = `${dbUrlBase}db${dbSuffix}.json`;
			const response = await fetch(dbUrlJson);
			const json = await response.json();
			const baseDb = UIDatabase.fromJson(json);

			// Clear items (we'll load them from API)
			baseDb.items = [];

			// Create database with base data (enchants, gems, zones)
			const db = new Database(baseDb);

			console.log(`Loaded ${baseDb.enchants.length} enchants, ${baseDb.gems.length} gems from local db`);

			// Load item sets
			await db.loadItemSets(language);

			// Load items ONLY from editor API
			try {
				const apiUrl = `${EDITOR_API_URL}/api/item-data?lang=${language}`;
				console.log('Fetching items from API:', apiUrl);
				const apiResp = await fetch(apiUrl);
				if (apiResp.ok) {
					const apiItems = await apiResp.json() as Item[];
					if (apiItems.length > 0) {
						for (const item of apiItems) {
							db.items.set(item.id, item);
							db.itemIcons[item.id] = Promise.resolve(IconData.create({
								id: item.id,
								name: item.name,
								icon: item.icon,
							}));
						}
						console.log(`Loaded ${apiItems.length} items from editor API`);
					} else {
						console.warn('API returned 0 items!');
					}
				} else {
					console.error('API request failed:', apiResp.status, apiResp.statusText);
				}
			} catch (e) {
				console.error('Editor API failed!', e);
			}

			return db;
		})();

		// Cache the promise immediately (before it resolves)
		Database.loadedDBs.set(language, loadPromise);
		return loadPromise;
	}

	// Switch language and reload database
	static async switchLanguage(newLang: string): Promise<void> {
		Database.currentLanguage = newLang;
		// Clear existing loaded databases and tooltips
		Database.clearTooltipCache();
		// Preload the new language database
		const db = await Database.get(newLang);
		// Warm up the cache by accessing items
		void db.getAllItems();
	}

	// Get local path for an icon
	static getIconPath(iconName: string): string {
		return `${iconsBasePath}${iconName}.jpg`;
	}

	static async getLeftovers(lang?: string): Promise<UIDatabase> {
		const language = lang || getLanguageCode() || 'en';
		const dbSuffix = language === 'en' ? '' : `_${language}`;
		const leftoversUrlJson = `${dbUrlBase}leftover_db${dbSuffix}.json`;
		const leftoversUrlBin = `${dbUrlBase}leftover_db${dbSuffix}.bin`;

		if (READ_JSON) {
			const response = await fetch(leftoversUrlJson);
			const json = await response.json();
			return UIDatabase.fromJson(json);
		} else {
			const response = await fetch(leftoversUrlBin);
			const buffer = await response.arrayBuffer();
			return UIDatabase.fromBinary(new Uint8Array(buffer));
		}
	}

	// Checks if any items in the equipment are missing from the current DB. If so, loads the leftover DB.
	static async loadLeftoversIfNecessary(equipment: EquipmentSpec, lang?: string): Promise<Database> {
		const language = lang || getLanguageCode() || 'en';
		const db = await Database.get(language);
		if (db.loadedLeftovers) {
			return db;
		}

		// Check if we're already loading leftovers for this language
		if (Database.loadingLeftovers.has(language)) {
			return Database.loadingLeftovers.get(language)!;
		}

		const shouldLoadLeftovers = equipment.items.some(item => item.id != 0 && !db.items.has(item.id));
		if (!shouldLoadLeftovers) {
			return db;
		}

		// Create and cache the loading promise
		const loadPromise = (async () => {
			const leftoverDb = await Database.getLeftovers(language);
			db.loadProto(leftoverDb);
			db.loadedLeftovers = true;
			Database.loadingLeftovers.delete(language); // Clean up after loading
			return db;
		})();

		Database.loadingLeftovers.set(language, loadPromise);
		return loadPromise;
	}

	private items = new Map<number, Item>();
	private readonly enchantsBySlot: Partial<Record<ItemSlot, Enchant[]>> = {};
	private readonly gems = new Map<number, Gem>();
	private readonly npcs = new Map<number, Npc>();
	private readonly zones = new Map<number, Zone>();
	private readonly presetEncounters = new Map<string, PresetEncounter>();
	private readonly presetTargets = new Map<string, PresetTarget>();
	private readonly itemIcons: Record<number, Promise<IconData>> = {};
	private readonly spellIcons: Record<number, Promise<IconData>> = {};
	private readonly glyphIds: Array<GlyphID> = [];
	private readonly itemSets = new Map<number, UISet>();
	private loadedLeftovers: boolean = false;

	private constructor(db: UIDatabase) {
		this.loadProto(db);
	}

	// Add all data from the db proto into this database.
	private loadProto(db: UIDatabase) {
		db.items.forEach(item => this.items.set(item.id, item));
		db.enchants.forEach(enchant => {
			const slots = getEligibleEnchantSlots(enchant);
			slots.forEach(slot => {
				if (!this.enchantsBySlot[slot]) {
					this.enchantsBySlot[slot] = [];
				}
				this.enchantsBySlot[slot]!.push(enchant);
			});
		});
		db.gems.forEach(gem => this.gems.set(gem.id, gem));

		db.npcs.forEach(npc => this.npcs.set(npc.id, npc));
		db.zones.forEach(zone => this.zones.set(zone.id, zone));
		db.encounters.forEach(encounter => this.presetEncounters.set(encounter.path, encounter));
		db.encounters.map(e => e.targets).flat().forEach(target => this.presetTargets.set(target.path, target));

		db.items.forEach(item => this.itemIcons[item.id] = Promise.resolve(IconData.create({
			id: item.id,
			name: item.name,
			icon: item.icon,
		})));
		db.gems.forEach(gem => this.itemIcons[gem.id] = Promise.resolve(IconData.create({
			id: gem.id,
			name: gem.name,
			icon: gem.icon,
		})));
		db.itemIcons.forEach(data => this.itemIcons[data.id] = Promise.resolve(data));
		// Spell icons are now localized in the database
		db.spellIcons.forEach(data => this.spellIcons[data.id] = Promise.resolve(data));
		db.glyphIds.forEach(id => this.glyphIds.push(id));
	}

	// Load item sets from editor API
	async loadItemSets(language: string): Promise<void> {
		try {
			const apiUrl = `${EDITOR_API_URL}/api/item-sets?lang=${language}`;
			console.log('Fetching item sets from API:', apiUrl);
			const apiResp = await fetch(apiUrl);
			if (apiResp.ok) {
				const apiSets = await apiResp.json() as UISet[];
				if (apiSets.length > 0) {
					apiSets.forEach(set => this.itemSets.set(set.id, set));
					console.log(`Loaded ${apiSets.length} item sets from editor API`);
				} else {
					console.warn('API returned 0 item sets!');
				}
			} else {
				console.error('API request failed:', apiResp.status, apiResp.statusText);
			}
		} catch (e) {
			console.error('Editor API for item sets failed!', e);
		}
	}

	getAllItems(): Array<Item> {
		return Array.from(this.items.values());
	}

	getItems(slot: ItemSlot): Array<Item> {
		const all = this.getAllItems().filter(item => getEligibleItemSlots(item).includes(slot));
		return all;
	}

	getEnchants(slot: ItemSlot): Array<Enchant> {
		return this.enchantsBySlot[slot] || [];
	}

	getGems(socketColor?: GemColor): Array<Gem> {
		if (!socketColor) 
			return Array.from(this.gems.values());

		let ret = new Array();
		for (let g of this.gems.values()){
			if (gemEligibleForSocket(g, socketColor))
				ret.push(g);
		}
		return ret;
	}

	getNpc(npcId: number): Npc | null {
		return this.npcs.get(npcId) || null;
	}
	getZone(zoneId: number): Zone | null {
		return this.zones.get(zoneId) || null;
	}

	getMatchingGems(socketColor: GemColor): Array<Gem> {
		let ret = new Array();
		for (let g of this.gems.values()){
			if (gemMatchesSocket(g, socketColor))
				ret.push(g);
		}
		return ret;
	}

	lookupGem(itemID: number): Gem | null {
		return this.gems.get(itemID) || null;
	}

	lookupItemSpec(itemSpec: ItemSpec): EquippedItem | null {
		const item = this.items.get(itemSpec.id);
		if (!item)
			return null;

		let enchant: Enchant | null = null;
		if (itemSpec.enchant) {
			const slots = getEligibleItemSlots(item);
			for (let i = 0; i < slots.length; i++) {
				enchant = (this.enchantsBySlot[slots[i]] || [])
					.find(enchant => [enchant.effectId, enchant.itemId, enchant.spellId].includes(itemSpec.enchant)) || null;
				if (enchant) {
					break;
				}
			}
		}

		const gems = itemSpec.gems.map(gemId => this.lookupGem(gemId));

		return new EquippedItem(item, enchant, gems);
	}

	lookupEquipmentSpec(equipSpec: EquipmentSpec): Gear {
		// EquipmentSpec is supposed to be indexed by slot, but here we assume
		// it isn't just in case.
		const gearMap: Partial<Record<ItemSlot, EquippedItem | null>> = {};

		equipSpec.items.forEach(itemSpec => {
			const item = this.lookupItemSpec(itemSpec);
			if (!item)
				return;

			const itemSlots = getEligibleItemSlots(item.item);

			const assignedSlot = itemSlots.find(slot => !gearMap[slot]);
			if (assignedSlot == null)
				throw new Error('No slots left to equip ' + Item.toJsonString(item.item));

			gearMap[assignedSlot] = item;
		});

		return new Gear(gearMap);
	}

	lookupItemSwap(itemSwap: ItemSwap): ItemSwapGear {
		return new ItemSwapGear({
			[ItemSlot.ItemSlotMainHand]: itemSwap.mhItem ? this.lookupItemSpec(itemSwap.mhItem): null,
			[ItemSlot.ItemSlotOffHand]: itemSwap.ohItem ? this.lookupItemSpec(itemSwap.ohItem): null,
			[ItemSlot.ItemSlotRanged]: itemSwap.rangedItem ? this.lookupItemSpec(itemSwap.rangedItem): null,
		});
	}

	enchantSpellIdToEffectId(enchantSpellId: number): number {
		const enchant = Object.values(this.enchantsBySlot).flat().find(enchant => enchant.spellId == enchantSpellId);
		return enchant ? enchant.effectId : 0;
	}

	glyphItemToSpellId(itemId: number): number {
		return this.glyphIds.find(gid => gid.itemId == itemId)?.spellId || 0;
	}
	glyphSpellToItemId(spellId: number): number {
		return this.glyphIds.find(gid => gid.spellId == spellId)?.itemId || 0;
	}

	getPresetEncounter(path: string): PresetEncounter | null {
		return this.presetEncounters.get(path) || null;
	}
	getPresetTarget(path: string): PresetTarget | null {
		return this.presetTargets.get(path) || null;
	}
	getAllPresetEncounters(): Array<PresetEncounter> {
		return Array.from(this.presetEncounters.values());
	}
	getAllPresetTargets(): Array<PresetTarget> {
		return Array.from(this.presetTargets.values());
	}

	// Get an item set by its ID
	getSetById(setId: number): UISet | undefined {
		return this.itemSets.get(setId);
	}

	// Get all item sets
	getAllSets(): Array<UISet> {
		return Array.from(this.itemSets.values());
	}

	static async getItemIconData(itemId: number): Promise<IconData> {
		const db = await Database.get();
		if (itemId in db.itemIcons) {
			return await db.itemIcons[itemId];
		}
		// Return default if not found
		return IconData.create({ id: itemId, name: '', icon: 'inv_misc_questionmark' });
	}

	static async getSpellIconData(spellId: number): Promise<IconData> {
		const db = await Database.get();
		// 检查缓存
		if (spellId in db.spellIcons) {
			return await db.spellIcons[spellId];
		}
		// 从 editor API 查询
		const promise = (async (): Promise<IconData> => {
			try {
				const lang = getLanguageCode() || 'cn';
				const resp = await fetch(`${EDITOR_API_URL}/api/spells/${spellId}?lang=${lang}`);
				if (resp.ok) {
					const data = await resp.json();
					return IconData.create({ id: data.id, name: data.name, icon: data.icon });
				}
			} catch (e) {
				console.warn(`Failed to fetch spell ${spellId} from editor API:`, e);
			}
			return IconData.create({ id: spellId, name: '', icon: 'inv_misc_questionmark' });
		})();
		// 缓存 promise，防止重复请求
		db.spellIcons[spellId] = promise;
		return promise;
	}

	// Clears the tooltip cache, useful when language changes
	static clearTooltipCache(): void {
		Database.loadedDBs.clear();
		Database.loadingLeftovers.clear();
		// Clear tooltip cache from tooltips module
		import('../tooltips.js').then(module => module.clearTooltipCache());
	}

	// Get current language code
	static getCurrentLanguage(): string {
		return Database.currentLanguage;
	}

	public static mergeSimDatabases(db1: SimDatabase, db2: SimDatabase): SimDatabase {
		return SimDatabase.create({
			items: distinct(db1.items.concat(db2.items), (a, b) => a.id == b.id),
			enchants: distinct(db1.enchants.concat(db2.enchants), (a, b) => a.effectId == b.effectId),
			gems: distinct(db1.gems.concat(db2.gems), (a, b) => a.id == b.id),
		})
	}
}
